import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, FormField, Input, Spinner } from '@nexvia-logistics/ui';
import { useAuth } from '@/shared/useAuth';
import { hasStorageProfile } from '@/pages/empresa/storage-api';
import { safeReturnTo } from '@/shared/return-to';
import { isEmailPasswordAuthEnabled } from '@/shared/auth-options';
import { useStorageModuleState } from '@/shared/useStorageModuleState';
import StorageModuleOffPage from '@/shared/StorageModuleOffPage';
import AuthSplitLayout from '@/pages/login/AuthSplitLayout';

/** Google's mark, at Google's colours — brand artwork, same SVG nexvia-backoffice's Login.tsx uses. */
const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

/**
 * The links (forgot password, register, back to sign-in) are `<button type="button">`s styled as
 * links: the design system has no link-shaped button (spec "Not adopted").
 */
const LINK_CLASS = 'inline-flex min-h-11 cursor-pointer items-center border-0 bg-transparent p-0 text-ui-label text-primary hover:underline';

const TITLE = 'Ingresá a tu depósito';
const DESCRIPTION = 'Gestioná tus solicitudes, cotizaciones y recepciones.';

/** An error above the buttons/fields, full column width (the web login's spacing). */
const Notice = ({ children }: { children: ReactNode }) => (
  <div className="mb-4 w-full">
    <Alert tone="critical">{children}</Alert>
  </div>
);

/** The `<form>`'s submit, as `AuthPage` did it: no native submit, the handler instead. */
const submitWith = (handler: () => void | Promise<void>) => (event: FormEvent<HTMLFormElement>) => {
  event.preventDefault();
  void handler();
};

/**
 * The only public route. Google sign-in or email + password in; out to the shell's default route
 * when the login already has a depósito account, or to `/registro` when it doesn't.
 *
 * NEX-1164 (2026-10-16) — decided by the business doc §5 (the depósito registers itself with its
 * RUT and razón social, and Nexvia approves it like a carrier): a login with no account
 * (`GET /storage/me` → 404 `STORAGE_PROFILE_NOT_FOUND`) goes to the registration form. Nothing is
 * created here any more — the old alta automática with placeholder data (`ensureStorageProfile`)
 * and the open question on self-registration it carried are gone. The check runs once, whichever way
 * the session arrived (Google, email + password, or an existing session visiting `/login`).
 *
 * NEX-1165 — a login with an account goes back to `?returnTo=` when the shell sent it here from a
 * protected page (`src/shared/return-to.ts`): the new-request mail's `/solicitudes?requestId=<id>`
 * ends on that request, not on the default route. A login with no account still goes to `/registro`.
 *
 * Storage feedback (2026-10-26): the screen is no longer the `AuthPage` card — it is the client web
 * login's split layout (`./login/AuthSplitLayout.tsx`), with the depósito's own headline, chip and
 * copy, so it looks like the rest of Nexvia and says whose panel it is. Only the presentation
 * changed: the same handlers, states and routing as before. The fields are still `FormField`+`Input`
 * inside a `<form>` with no `noValidate`: the inputs carry no `required`, so an empty submit still
 * reaches these handlers, and `type="email"` lets the browser reject a malformed non-empty address
 * before Firebase does (spec D-1.1). The web's legal line is not drawn: this app has no Terms/Privacy
 * links of its own.
 *
 * NEX-1645 — with the module `off` (`GET /app/features`) the page shows "todavía no está
 * disponible" instead of the sign-in, and a session that is already open is not routed into the
 * panel (it may sign out from there). While the state loads, a spinner: the sign-in never flashes.
 */
export default function Login() {
  const { user, isAuthenticated, authError, signInWithEmail, signInWithGoogle, sendPasswordReset, clearAuthError, logout } =
    useAuth();
  const moduleState = useStorageModuleState();
  const moduleOpen = moduleState !== null && moduleState !== 'off';
  // NEX-1164 — an email + password login whose email is not verified yet: the API refuses it
  // (`AUTH_EMAIL_NOT_VERIFIED`), so it goes to /registro's verification step without asking.
  const emailUnverified = user?.emailVerified === false;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = safeReturnTo(searchParams.get('returnTo')) ?? '/';
  const [mode, setMode] = useState<'login' | 'reset' | 'reset-sent'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [googleSubmitting, setGoogleSubmitting] = useState(false);
  // One check per mount: `isAuthenticated` flips during the Google popup too, so the effect below
  // and `handleGoogleSignIn` may both ask — the ref makes the second ask a no-op.
  const routingRef = useRef(false);

  const routeSignedInUser = useCallback(async () => {
    if (routingRef.current) return;
    routingRef.current = true;
    if (emailUnverified) {
      navigate('/registro', { replace: true });
      return;
    }
    let destination = returnTo;
    try {
      destination = (await hasStorageProfile()) ? returnTo : '/registro';
    } catch {
      // [ASSUMED] any other failure (network, 5xx) is not proof the account is missing: the shell's
      // pages show their own error and retry, which beats a registration form for an account
      // that may well exist.
    }
    navigate(destination, { replace: true });
  }, [navigate, returnTo, emailUnverified]);

  useEffect(() => {
    if (isAuthenticated && moduleOpen) {
      void routeSignedInUser();
    }
  }, [isAuthenticated, moduleOpen, routeSignedInUser]);

  const handleLogin = async () => {
    setSubmitting(true);
    try {
      await signInWithEmail(email, password);
    } catch {
      /* authError is already set by useAuth; nothing else to do here */
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleSubmitting(true);
    try {
      await signInWithGoogle();
    } catch {
      // A closed popup is silent; every other Firebase failure is already in authError
      // (useAuth's signInWithGoogle sets it). Nothing reached the API, so there is nothing to route.
      setGoogleSubmitting(false);
      return;
    }
    await routeSignedInUser();
  };

  const handleReset = async () => {
    setResetError(null);
    setSubmitting(true);
    try {
      await sendPasswordReset(email);
      setMode('reset-sent');
    } catch (err) {
      const code = (err as { code?: string } | null)?.code ?? '';
      if (code === 'auth/invalid-email') {
        setResetError('Ese correo no es válido.');
      } else {
        // Deliberately the same outcome as success: do not reveal whether the account exists.
        setMode('reset-sent');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const switchToReset = () => {
    clearAuthError();
    setResetError(null);
    setMode('reset');
  };

  const switchToLogin = () => {
    clearAuthError();
    setResetError(null);
    setMode('login');
  };

  if (moduleState === null) {
    return (
      <div className="grid min-h-dvh place-items-center bg-surface-container">
        <Spinner size={32} label="Cargando…" />
      </div>
    );
  }

  if (moduleState === 'off') {
    return <StorageModuleOffPage onSignOut={isAuthenticated ? () => void logout() : undefined} />;
  }

  const backToLogin = (
    <button type="button" onClick={switchToLogin} className={LINK_CLASS}>
      Volver a iniciar sesión
    </button>
  );

  if (mode === 'reset-sent') {
    return (
      <AuthSplitLayout
        title="Revisá tu correo"
        description={
          <>
            Si <strong className="wrap-anywhere">{email}</strong> tiene una cuenta de depósito, te enviamos instrucciones para restablecer tu
            contraseña.
          </>
        }
      >
        {backToLogin}
      </AuthSplitLayout>
    );
  }

  if (mode === 'reset') {
    return (
      <AuthSplitLayout title="Recuperar contraseña" description="Te enviamos un correo para elegir una nueva.">
        {resetError ? <Notice>{resetError}</Notice> : null}
        <form onSubmit={submitWith(handleReset)} className="mb-6 flex w-full flex-col gap-4">
          <FormField label="Correo electrónico">
            {(field) => (
              <Input
                {...field}
                type="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="tu@empresa.com"
              />
            )}
          </FormField>
          <Button type="submit" variant="primary" fullWidth showArrow={false} disabled={submitting}>
            {submitting ? 'Enviando…' : 'Enviar instrucciones'}
          </Button>
        </form>
        {backToLogin}
      </AuthSplitLayout>
    );
  }

  // The web login's provider button: default (`md`) size, full width — the wide pill.
  const googleButton = (
    <Button type="button" variant="neutral" fullWidth showArrow={false} onClick={handleGoogleSignIn} disabled={googleSubmitting}>
      <GoogleIcon />
      {googleSubmitting ? 'Ingresando…' : 'Continuar con Google'}
    </Button>
  );

  const signUpLink = (
    // NEX-1164 — the panel's own sign-up, on /registro.
    <button
      type="button"
      onClick={() => {
        clearAuthError();
        navigate('/registro');
      }}
      className={LINK_CLASS}
    >
      Registrá tu depósito
    </button>
  );

  // Storage feedback (2026-10-27): with Google-only sign-in, signing up IS signing in with Google — a
  // login with no depósito account already lands on /registro's form. Going through /registro first
  // only added a "Registrarte con Google" screen, so this link opens Google directly.
  const googleSignUpLink = (
    <button
      type="button"
      onClick={() => {
        clearAuthError();
        void handleGoogleSignIn();
      }}
      disabled={googleSubmitting}
      className={LINK_CLASS}
    >
      ¿No tenés cuenta? Registrá tu depósito
    </button>
  );

  // Product decision 2026-10-19: Google-only sign-in. The email + password form below is hidden,
  // not removed (`shared/auth-options.ts`).
  if (!isEmailPasswordAuthEnabled()) {
    return (
      <AuthSplitLayout title={TITLE} description={DESCRIPTION}>
        {authError ? <Notice>{authError}</Notice> : null}
        <div className="mb-6 flex w-full flex-col gap-4">{googleButton}</div>
        {googleSignUpLink}
      </AuthSplitLayout>
    );
  }

  return (
    <AuthSplitLayout title={TITLE} description={DESCRIPTION}>
      {authError ? <Notice>{authError}</Notice> : null}
      <div className="flex w-full flex-col gap-4">{googleButton}</div>
      <div className="my-5 flex w-full items-center gap-3 text-ui-small text-on-surface-variant">
        <span className="h-px flex-1 bg-outline-muted" />o<span className="h-px flex-1 bg-outline-muted" />
      </div>
      <form onSubmit={submitWith(handleLogin)} className="mb-6 flex w-full flex-col gap-4">
        <FormField label="Correo electrónico">
          {(field) => (
            <Input
              {...field}
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="tu@empresa.com"
            />
          )}
        </FormField>
        <FormField label="Contraseña">
          {(field) => (
            <Input
              {...field}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="••••••••"
            />
          )}
        </FormField>
        <Button type="submit" variant="primary" fullWidth showArrow={false} disabled={submitting}>
          {submitting ? 'Ingresando…' : 'Iniciar sesión'}
        </Button>
      </form>
      <span className="flex flex-wrap justify-center gap-4">
        <button type="button" onClick={switchToReset} className={LINK_CLASS}>
          ¿Olvidaste tu contraseña?
        </button>
        {signUpLink}
      </span>
    </AuthSplitLayout>
  );
}
